import { useEffect, useRef } from 'react';
import { play } from './engine.js';
import { sfx } from '../sfx/index.js';
import type { SfxName } from '../sfx/index.js';
import type { CelebrationOptions, CelebrationType } from './types.js';
import './celebrations.css';

interface CelebrationLayerProps {
  /** Efecto a reproducir cuando `trigger` pasa a verdadero. */
  type: CelebrationType;
  /**
   * Dispara el efecto en la TRANSICIÓN a verdadero (falsy → truthy), una sola
   * vez. Mantenerlo en `true` NO relanza nada: el re-disparo por render es el
   * leak clásico de las celebraciones y esta capa lo evita por diseño.
   *
   * Para volver a disparar, pasá un número que cambie (p. ej. un contador) —
   * cada valor nuevo y truthy cuenta como una transición.
   */
  trigger: boolean | number;
  /** Opciones del efecto (sin `target`: lo provee esta capa). */
  options?: Omit<CelebrationOptions, 'target'>;
  /** z-index de la capa contenedora (def: 40, debajo de modales). */
  z?: number;
  /**
   * SFX a reproducir en la MISMA transición que el visual (acoplado, para no
   * doble-disparar). Respeta el mute de SFX (`orborix.sfxMuted`) por su cuenta.
   */
  sound?: SfxName;
}

/**
 * Capa de celebración contenida y leak-safe.
 *
 * Renderiza un host `absolute inset:0` (no interactivo) dentro de su contenedor
 * y reproduce el efecto adentro, de modo que las partículas quedan recortadas
 * al recuadro de la app en vez de cubrir toda la ventana. Garantías:
 *
 *  1. Dispara solo en la transición de `trigger` (ref con el valor previo) →
 *     nunca relanza por re-render.
 *  2. Stopea el control anterior antes de lanzar uno nuevo → una celebración
 *     a la vez por capa.
 *  3. Limpia (`stop()`) en el unmount → al salir de la pantalla a mitad del
 *     efecto se cancela el rAF y se remueve el canvas.
 *
 * El contenedor padre debe tener `position: relative` (o la capa cae al primer
 * ancestro posicionado). El efecto fuerza `position: relative` en el host si
 * hiciera falta.
 */
export function CelebrationLayer({ type, trigger, options, z = 40, sound }: CelebrationLayerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const ctrlRef = useRef<{ stop: () => void } | null>(null);
  const prevTrigger = useRef<boolean | number>(false);

  useEffect(() => {
    // Dispara solo cuando `trigger` cambia a un valor truthy distinto del
    // anterior: falsy→truthy, o truthy→truthy-nuevo (contador). Mantenerse en
    // el mismo truthy (re-render con options inline, etc.) no relanza nada.
    const isTruthy = Boolean(trigger);
    const changed = trigger !== prevTrigger.current;
    prevTrigger.current = trigger;
    if (!isTruthy || !changed) return;

    if (sound) sfx.play(sound);

    const host = hostRef.current;
    if (!host) return;
    ctrlRef.current?.stop();
    ctrlRef.current = play(type, { ...options, target: host, z });
    // No limpiamos en el cleanup de ESTE efecto (correría en cada cambio de
    // deps); la limpieza vive en el efecto de unmount de abajo.
  }, [trigger, type, z, options, sound]);

  // Limpieza al desmontar: cancela el efecto en vuelo (cambio de ruta, etc.).
  useEffect(() => {
    return () => {
      ctrlRef.current?.stop();
      ctrlRef.current = null;
    };
  }, []);

  return <div ref={hostRef} className="cel-host" aria-hidden="true" />;
}
